import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ShieldCheck,
  Star,
  Users,
  Phone,
  MapPin,
  Activity,
  Droplet,
  Droplets,
  HeartPulse,
  FlaskConical,
  TestTube,
  Microscope,
  Pill,
  Sparkles,
  Bone,
  Sun,
  Gauge,
  Atom,
} from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { BookingForm } from "@/components/BookingForm";
import { contact, locations } from "@/lib/healic-data";
import { locationHref } from "@/lib/location-routes";
import heroImgAsset from "@/assets/path-lab/hero.jpg.asset.json";
import homeLabAsset from "@/assets/path-lab/home-lab.jpg.asset.json";
const heroImg = heroImgAsset.url;
const homeLab = homeLabAsset.url;
import hospitalGurugram from "@/assets/hospital-gurugram.webp";
import hospitalGhaziabad from "@/assets/hospital-ghaziabad.webp";
import hospitalHaridwar from "@/assets/hospital-haridwar.webp";
import hospitalAgra from "@/assets/hospital-agra.webp";
import hospitalExterior from "@/assets/hospital-exterior.jpg";

const SITE_URL = "https://healic.in";

export const Route = createFileRoute("/path-lab")({
  head: () => ({
    meta: [
      { title: "Path Lab — NABL Certified Diagnostics | Healic Multispeciality Hospital" },
      { name: "description", content: "NABL certified Healic Path Lab — curated health checkups, popular tests and home sample collection. Trusted by 35,000+ patients with 4.9/5 Google rating." },
      { property: "og:title", content: "Path Lab — Healic Multispeciality Hospital" },
      { property: "og:description", content: "Accurate and timely diagnostics with advanced pathology testing for blood, urine and tissue analysis." },
      { property: "og:image", content: `${SITE_URL}/_l/path-lab-hero.jpg` },
      { property: "og:url", content: `${SITE_URL}/path-lab` },
      { property: "og:type", content: "website" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/path-lab` }],
  }),
  component: PathLabPage,
});

type Checkup = {
  id: string;
  name: string;
  tier: string;
  params: number;
  price: number;
  mrp: number;
  gradient: string;
  accent: string;
  tests: string[];
};

const checkups: Checkup[] = [
  {
    id: "basic",
    name: "Healic Full Body Checkup - Basic",
    tier: "BASIC",
    params: 68,
    price: 699,
    mrp: 1999,
    gradient: "from-sky-50 to-sky-100",
    accent: "bg-primary text-primary-foreground",
    tests: ["Liver Function test", "Kidney function test", "Urine Routine & Microscopy Extended", "Complete Blood Count", "Random Blood Sugar", "ESR"],
  },
  {
    id: "silver",
    name: "Healic Full Body Checkup - Silver",
    tier: "SILVER",
    params: 80,
    price: 1499,
    mrp: 2999,
    gradient: "from-slate-50 to-slate-200",
    accent: "bg-slate-600 text-white",
    tests: ["All From Basics", "HbA1C", "Lipid Profile Basic", "Thyroid Profile", "Iron Studies"],
  },
  {
    id: "gold",
    name: "Healic Full Body Checkup - Gold",
    tier: "GOLD",
    params: 87,
    price: 2499,
    mrp: 5799,
    gradient: "from-amber-50 to-amber-100",
    accent: "bg-amber-500 text-white",
    tests: ["All From Silver", "Vitamin D", "Vitamin B12", "Complete Blood Count", "Ferritin"],
  },
  {
    id: "platinum",
    name: "Healic Full Body Checkup - Platinum",
    tier: "PLATINUM",
    params: 90,
    price: 3699,
    mrp: 6999,
    gradient: "from-rose-50 to-rose-100",
    accent: "bg-rose-500 text-white",
    tests: ["All From Gold", "Amylase Enzymatic, Serum", "RA Factor", "CRP (C Reactive Protein) Quantitative, Serum"],
  },
  {
    id: "fever",
    name: "Healic Fever Profile Package",
    tier: "FEVER",
    params: 48,
    price: 980,
    mrp: 1999,
    gradient: "from-orange-50 to-orange-100",
    accent: "bg-orange-500 text-white",
    tests: ["Malaria Antigen, Vivax & Falciparum", "Typhi Dot IgM", "Dengue IgG & IgM (Rapid Card)", "Urine Routine & Microscopy Extended", "Complete Blood Count"],
  },
  {
    id: "male",
    name: "Healic Extensive Care Checkup - Male",
    tier: "MALE CHECK-UP",
    params: 95,
    price: 3575,
    mrp: 6999,
    gradient: "from-blue-50 to-blue-100",
    accent: "bg-primary text-primary-foreground",
    tests: ["Liver Function test", "Thyroid profile", "Iron Studies", "Kidney Function Test Advance", "HbA1C", "Urine Routine & Microscopy Extended", "Complete Blood Count", "Lipid profile", "Blood glucose Fasting", "HsCRP High sensitivity CRP", "Vitamin B12 Cyanocobalamin", "Vitamin D Total-25 Hydroxy"],
  },
  {
    id: "female",
    name: "Healic Extensive Care Checkup - Female",
    tier: "FEMALE CHECK-UP",
    params: 98,
    price: 4299,
    mrp: 6999,
    gradient: "from-pink-50 to-pink-100",
    accent: "bg-pink-500 text-white",
    tests: ["Liver Function test", "Thyroid profile", "Iron Studies", "Kidney Function Test Advance", "HbA1C", "Lipid profile", "Vitamin B12", "Vitamin D", "CA-125, Serum", "FSH", "LH"],
  },
  {
    id: "diabetic",
    name: "Healic Diabetic Checkup - Basic",
    tier: "DIABETIC",
    params: 42,
    price: 635,
    mrp: 1999,
    gradient: "from-emerald-50 to-emerald-100",
    accent: "bg-emerald-600 text-white",
    tests: ["Kidney Function Test Advance", "HbA1C", "Urine Routine", "Lipid profile", "Blood glucose Fasting"],
  },
];

const trustBadges = [
  { icon: ShieldCheck, title: "NABL Certified Lab", desc: "Validated quality and patient trust." },
  { icon: Users, title: "35k+ Satisfied Patients", desc: "Trusted across all Healic locations." },
  { icon: Star, title: "4.9/5 Google Rating", desc: "Loved by patients and families." },
];

const popularTests = [
  { name: "Liver Function test", icon: FlaskConical },
  { name: "Kidney function test", icon: Droplet },
  { name: "Complete Blood Count", icon: Droplets },
  { name: "Random Blood Sugar", icon: Gauge },
  { name: "ESR", icon: TestTube },
  { name: "HbA1C", icon: Gauge },
  { name: "Lipid Profile Basic", icon: HeartPulse },
  { name: "Urine Routine & Microscopy Extended", icon: Microscope },
  { name: "CRP", icon: Activity },
  { name: "RA Test Rheumatoid Arthritis Factor, Quantitative", icon: Bone },
  { name: "Amylase Enzymatic, Serum", icon: FlaskConical },
  { name: "Ferritin", icon: Atom },
  { name: "Iron Studies", icon: Atom },
  { name: "Vitamin B12 Cyanocobalamin", icon: Pill },
  { name: "Vitamin D Total-25 Hydroxy", icon: Sun },
  { name: "Thyroid Profile-Total", icon: Sparkles },
];

const locationImages: Record<string, string> = {
  "sohna-road-gurugram": hospitalGurugram,
  "indirapuram-ghaziabad": hospitalGhaziabad,
  haridwar: hospitalHaridwar,
  agra: hospitalAgra,
  muzaffarnagar: hospitalExterior,
};

export function PathLabPage() {
  return (
    <PageShell>
      {/* Hero */}
      <section className="relative">
        <div className="relative h-[240px] w-full overflow-hidden sm:h-[300px]">
          <img
            src={heroImg}
            alt="Healic Path Lab — blood sample collection"
            className="h-full w-full object-cover"
            loading="eager"
            width={1600}
            height={800}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground/80 via-foreground/55 to-transparent" />
          <div className="absolute inset-0 mx-auto flex max-w-7xl items-center px-4 lg:px-8">
            <div className="max-w-xl text-background">
              <span className="inline-flex items-center gap-2 rounded-pill bg-background/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide backdrop-blur">
                Healic Path Lab
              </span>
              <h1 className="mt-2 font-display text-xl font-bold leading-tight sm:text-3xl">
                Path Lab
              </h1>
              <p className="mt-2 max-w-md text-xs opacity-90 sm:text-sm">
                Accurate and timely diagnostics with advanced pathology testing for blood, urine, and tissue analysis.
              </p>
              <Link
                to="/contact"
                className="mt-4 inline-flex items-center rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-cta transition hover:opacity-90"
              >
                Request Call Back
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* You can trust us */}
      <section className="mx-auto max-w-7xl px-4 py-10 lg:px-8">
        <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
          You can trust us!
        </h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {trustBadges.map((b) => (
            <div
              key={b.title}
              className="flex items-center gap-4 rounded-2xl border border-border bg-card p-5 shadow-card"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
                <b.icon className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-display text-base font-semibold text-foreground">
                  {b.title}
                </h3>
                <p className="text-sm text-muted-foreground">{b.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Popular Tests — moved above checkups */}
      <section className="bg-muted/40">
        <div className="mx-auto max-w-7xl px-4 py-10 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-accent">
                Most booked
              </p>
              <h2 className="mt-2 font-display text-2xl font-bold text-foreground sm:text-3xl">
                Popular Tests
              </h2>
            </div>
            <Link
              to="/contact"
              className="text-sm font-semibold text-primary hover:text-accent"
            >
              Book a custom test →
            </Link>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {popularTests.map((t) => (
              <Link
                key={t.name}
                to="/contact"
                className="group flex items-start gap-4 rounded-2xl border border-border bg-card p-5 shadow-soft transition hover:-translate-y-0.5 hover:border-accent hover:shadow-card"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary transition group-hover:bg-primary group-hover:text-primary-foreground">
                  <t.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-display text-sm font-semibold text-foreground">
                    {t.name}
                  </h3>
                  <p className="mt-0.5 text-xs text-muted-foreground">Book test</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Curated Health Checkups — clean grid, no portraits */}
      <section className="mx-auto max-w-7xl px-4 py-10 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">
              Packages
            </p>
            <h2 className="mt-2 font-display text-2xl font-bold text-foreground sm:text-3xl">
              Curated Health Checkups
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              Hand-picked panels for every life stage — transparent prices, NABL-grade reports.
            </p>
          </div>
        </div>

        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {checkups.map((c) => {
            const savings = c.mrp - c.price;
            return (
              <article
                key={c.id}
                className={`group relative flex flex-col overflow-hidden rounded-3xl border border-border bg-gradient-to-br ${c.gradient} p-6 shadow-soft transition hover:-translate-y-1 hover:shadow-card`}
              >
                <div className="flex items-start justify-between gap-3">
                  <span
                    className={`inline-flex items-center rounded-pill px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${c.accent}`}
                  >
                    {c.tier}
                  </span>
                  <span className="rounded-pill bg-background/80 px-2.5 py-1 text-[10px] font-semibold text-foreground/80">
                    {c.params} tests
                  </span>
                </div>

                <h3 className="mt-5 font-display text-lg font-bold leading-tight text-foreground">
                  {c.name}
                </h3>

                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display text-2xl font-bold text-foreground">
                    ₹{c.price}
                  </span>
                  <span className="text-xs text-muted-foreground line-through">₹{c.mrp}</span>
                  <span className="ml-auto rounded-pill bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                    Save ₹{savings}
                  </span>
                </div>

                <ul className="mt-5 space-y-1.5 text-xs text-foreground/80">
                  {c.tests.slice(0, 4).map((t) => (
                    <li key={t} className="flex gap-2">
                      <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                      <span className="line-clamp-1">{t}</span>
                    </li>
                  ))}
                  {c.tests.length > 4 && (
                    <li className="pl-3.5 text-[11px] font-semibold text-accent">
                      + {c.tests.length - 4} more tests
                    </li>
                  )}
                </ul>

                <Link
                  to="/contact"
                  className="mt-6 inline-flex items-center justify-center rounded-xl bg-primary px-5 py-2.5 text-xs font-semibold text-primary-foreground shadow-cta transition hover:opacity-90"
                >
                  Book Now
                </Link>
              </article>
            );
          })}
        </div>
      </section>

      {/* 15% Off Membership banner */}
      <section className="mx-auto max-w-5xl px-4 pb-12 lg:px-8">
        <div className="grid items-stretch gap-0 overflow-hidden rounded-2xl bg-primary text-primary-foreground shadow-card md:grid-cols-2">
          <div className="flex flex-col justify-center px-5 py-6 sm:px-8 sm:py-8 lg:px-10 lg:py-9">
            <h2 className="font-display text-2xl font-bold sm:text-3xl lg:text-4xl">
              15% Off
            </h2>
            <p className="mt-1.5 text-sm opacity-90">On Home Lab Tests</p>
            <Link
              to="/healic-plus"
              className="mt-4 inline-flex w-fit items-center rounded-xl bg-background px-4 py-2 text-sm font-semibold text-primary shadow-cta transition hover:opacity-90"
            >
              Buy Membership
            </Link>
            <p className="mt-4 text-xs opacity-75">
              *Special Discount On Subscriptions*
            </p>
          </div>
          <div className="h-full min-h-[140px] sm:min-h-[170px] md:max-h-[260px]">
            <img
              src={homeLab}
              alt="Home lab test with Healic+ membership"
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </section>



      {/* Find Us in Your City */}
      <section className="mx-auto max-w-7xl px-4 pb-16 lg:px-8">
        <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
          Find Us in Your City
        </h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {locations.map((l) => (
            <Link
              key={l.slug}
              to={locationHref(l.slug)}
              className="group overflow-hidden rounded-3xl border border-border bg-card shadow-card transition hover:shadow-card-hover"
            >
              <div className="aspect-[16/10] overflow-hidden bg-primary-soft">
                <img
                  src={locationImages[l.slug]}
                  alt={`Healic ${l.city}`}
                  loading="lazy"
                  className="h-full w-full object-cover transition group-hover:scale-[1.03]"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-accent" />
                  <h3 className="font-display text-base font-semibold text-foreground">
                    {l.city}
                  </h3>
                </div>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-3">
                  {l.address}
                </p>
              </div>
            </Link>
          ))}
        </div>

        <div className="relative mt-8 overflow-hidden rounded-2xl border border-primary/10 bg-gradient-to-r from-primary-soft via-cream to-accent-soft p-6 shadow-card sm:p-8">
          <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-accent/20 blur-3xl" aria-hidden />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-44 w-44 rounded-full bg-primary/15 blur-3xl" aria-hidden />
          <div className="relative flex flex-col items-start justify-between gap-5 sm:flex-row sm:items-center">
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-cta">
                <Phone className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
                  Talk to an expert
                </p>
                <h3 className="mt-1 font-display text-lg font-bold text-foreground sm:text-xl">
                  Need help choosing the right test?
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Our health advisors are available 7 Am to 12 Am to guide you.
                </p>
              </div>
            </div>
            <a
              href={`tel:${contact.phoneTel}`}
              className="inline-flex items-center gap-2 rounded-xl bg-accent px-6 py-3 text-sm font-semibold text-accent-foreground shadow-cta transition hover:opacity-90"
            >
              <Phone className="h-4 w-4" /> {contact.phone}
            </a>
          </div>
        </div>
      </section>

      <BookingForm title="Book Your Path Lab Test" />
    </PageShell>
  );
}
